import React, {useCallback, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import {Card} from "../../../../components/card/card.jsx";
import citizenAggressorsListService from "../../../../services/web/citizen-aggressors-list-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {useLoaderData} from "react-router";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaSearch} from "react-icons/fa";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import AggressorsListModal from "./components/aggressors-list-modal.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";

const NUMERIC_FIELDS = ["total", "bogcmTotal", "encounterTotal", "occTotal", "visitTotal"]

const initialFilter = {
    cpf: "",
    name: "",
    total: "",
    bogcmTotal: "",
    encounterTotal: "",
    occTotal: "",
    visitTotal: ""
}

const BO_PERMISSIONS = PermissionEnum.BOGCM_WEB;
const BO_VIEW_PERMISSIONS = [PermissionEnum.BOGCM_VIEW_ALL, PermissionEnum.BOGCM_VIEW_SUBORDINATES, PermissionEnum.BOGCM_VIEW_MY];
const ENCOUNTER_PERMISSION = PermissionEnum.ENCOUNTER;
const OCC_PERMISSION = [PermissionEnum.OCCURRENCE_INTELLIGENCE, PermissionEnum.OCCURRENCE];

const AggressorsList = () => {
    const loaderData = useLoaderData()
    const hasPermission = useHasPermission();

    const [filter, setFilter] = useState(initialFilter)
    const [appliedFilter, setAppliedFilter] = useState(initialFilter)

    const [aggressor, setAggressor] = useState(undefined)
    const [target, setTarget] = useState(undefined)
    const [modal, setModal] = useState(false)

    const {data: dataFetch, isLoading} = useQuery({
        queryFn: () => citizenAggressorsListService.list(),
        queryKey: ["aggressors-list"]
    });

    const boName = loaderData?.bo_name
    const hasDispatch = loaderData?.hasDispatch

    const gridRef = useRef(undefined)

    const theme = useMemo(() => aggridUtils.generalTheme(), [])

    const [columnDefs] = useState([
        {field: "cpf", headerName: "CPF", width: 180, valueFormatter: ({value}) => projectUtils.formatCpfToView(value), comparator: aggridUtils.nullLastComparator},
        {field: "name", headerName: "Nome", flex: 1, comparator: aggridUtils.nullLastComparator, sort: "asc"},
        {field: "total", headerName: "SOS", width: 100, comparator: aggridUtils.nullLastComparator},
        {field: "bogcmTotal", headerName: boName, width: 110, hide: (!hasDispatch || !hasPermission(BO_PERMISSIONS) || !hasPermission(BO_VIEW_PERMISSIONS)), comparator: aggridUtils.nullLastComparator},
        {field: "encounterTotal", headerName: "Abordagens", width: 120, hide: !hasPermission(ENCOUNTER_PERMISSION), comparator: aggridUtils.nullLastComparator},
        {field: "occTotal", headerName: "Ocorrências", width: 130, hide: !hasPermission(OCC_PERMISSION), comparator: aggridUtils.nullLastComparator},
        {field: "visitTotal", headerName: "Visitas", width: 130, comparator: aggridUtils.nullLastComparator}
    ]);

    const data = useMemo(() => {
        if (!dataFetch) return []

        const predicates = []

        const cpfDigits = appliedFilter.cpf.replace(/\D/g, "")
        if (cpfDigits) predicates.push(d => (d.cpf ?? "").includes(cpfDigits))

        if (appliedFilter.name) {
            const name = appliedFilter.name.toLowerCase()
            predicates.push(d => (d.name ?? "").toLowerCase().includes(name))
        }

        for (const field of NUMERIC_FIELDS) {
            const value = appliedFilter[field]
            if (value) predicates.push(d => Number(d[field] ?? 0) === Number(value))
        }

        if (predicates.length === 0) return dataFetch
        return dataFetch.filter(d => predicates.every(p => p(d)))
    }, [dataFetch, appliedFilter])

    const apply = useCallback(() => setAppliedFilter(filter), [filter])

    const clear = useCallback(() => {
        setFilter(initialFilter)
        setAppliedFilter(initialFilter)
    }, [])

    const onChange = ({target: {id, value}}) => setFilter(prev => ({...prev, [id]: value}))

    useGlobalKey("Enter", apply)
    useGlobalKey("Escape", clear)

    const onCellClicked = ({data, colDef}) => {
        const cpf = data.cpf
        const field = colDef.field;
        if (!cpf || !["total", "bogcmTotal", "encounterTotal", "occTotal", "visitTotal"].includes(colDef.field) || !data[field]) {
            return
        }

        setAggressor(data.cpf)
        setTarget(field)
        setModal(true)
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Lista de Agressores</Template.Title>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 gap-3"}>
                        <InputForm
                            classNameWrapper={"col-span-2"}
                            id={"cpf"}
                            label={"CPF"}
                            mask={MaskDefault.cpf}
                            value={filter.cpf}
                            onChange={onChange}
                        />
                        <InputForm
                            classNameWrapper={"col-span-4"}
                            id={"name"}
                            label={"Nome"}
                            value={filter.name}
                            onChange={onChange}
                        />
                        <InputForm
                            classNameWrapper={"col-span-1"}
                            id={"total"}
                            label={"SOS"}
                            type={"number"}
                            min={0}
                            value={filter.total}
                            onChange={onChange}
                        />
                        {
                            hasDispatch && hasPermission(BO_PERMISSIONS) && hasPermission(BO_VIEW_PERMISSIONS) &&
                            <InputForm
                                classNameWrapper={"col-span-1"}
                                id={"bogcmTotal"}
                                label={boName}
                                type={"number"}
                                min={0}
                                value={filter.bogcmTotal}
                                onChange={onChange}
                            />
                        }
                        {
                            hasPermission(ENCOUNTER_PERMISSION) &&
                            <InputForm
                                classNameWrapper={"col-span-1"}
                                id={"encounterTotal"}
                                label={"Abordagens"}
                                type={"number"}
                                min={0}
                                value={filter.encounterTotal}
                                onChange={onChange}
                            />
                        }
                        {
                            hasPermission(OCC_PERMISSION) &&
                            <InputForm
                                classNameWrapper={"col-span-1"}
                                id={"occTotal"}
                                label={"Ocorrências"}
                                type={"number"}
                                min={0}
                                value={filter.occTotal}
                                onChange={onChange}
                            />
                        }

                        <div className={"col-span-2 flex gap-4"}>
                            <InputForm
                                classNameWrapper={"w-full"}
                                id={"visitTotal"}
                                label={"Visitas"}
                                type={"number"}
                                min={0}
                                value={filter.visitTotal}
                                onChange={onChange}
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear}>
                                    <FaEraser />
                                </Button>

                                <Button onClick={apply}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={32}
                                rowData={data}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={theme}
                            />
                        </div>
                    </Card.Body>
                </Card>

                <AggressorsListModal
                    aggressor={aggressor}
                    target={target}
                    open={modal}
                    boName={boName}
                    setOpen={() => {
                        setModal(false)
                        setTarget(undefined)
                        setAggressor(undefined)
                    }}
                />
            </Template.Body>
        </Template>
    );
};

export default AggressorsList;